<template>
  <b-modal
    :id="id"
    ref="orcamentoItemEditModal"
    size="lg"
    centered
    no-close-on-backdrop
    content-class="orcamento-item-edit"
    header-class="orcamento-item-edit__header"
    body-class="orcamento-item-edit__body"
    footer-class="orcamento-item-edit__footer"
    @close="close()"
  >
    <template v-slot:modal-header>
      <div class="orcamento-item-edit__header-inner">
        <div class="orcamento-item-edit__header-text">
          <span class="orcamento-item-edit__eyebrow">Item do Orçamento</span>
          <h3 class="orcamento-item-edit__produto">{{ item.titulo }}</h3>
        </div>
        <button
          type="button"
          class="orcamento-item-edit__close"
          :disabled="isSaving"
          @click="close()"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>
    </template>

    <template v-slot:default>
      <div class="orcamento-item-edit__grid">
        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="valor_unitario">Valor unitário</label>
          <b-form-input
            id="valor_unitario"
            type="text"
            :value="formatMoney(item.valor_unitario)"
            readonly
            class="orcamento-item-edit__input text-right"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="quantidade">Quantidade</label>
          <b-form-input
            id="quantidade"
            type="number"
            :min="item.multiplo"
            :step="item.multiplo"
            v-model="item.quantidade"
            class="orcamento-item-edit__input text-center"
            placeholder="Quantidade"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label">Margem</label>
          <div class="orcamento-item-edit__margem">
            <div class="orcamento-item-edit__toggle" role="group" aria-label="Tipo de margem">
              <button
                type="button"
                class="orcamento-item-edit__toggle-btn"
                :class="{ 'orcamento-item-edit__toggle-btn--active': isMargemValor }"
                @mousedown.prevent
                @click="setMargemTipo('V')"
              >
                R$
              </button>
              <button
                type="button"
                class="orcamento-item-edit__toggle-btn"
                :class="{ 'orcamento-item-edit__toggle-btn--active': !isMargemValor }"
                @mousedown.prevent
                @click="setMargemTipo('P')"
              >
                %
              </button>
            </div>
            <money
              v-show="isMargemValor"
              id="margem_valor"
              @blur.native="onBlurMargem_valor"
              type="text"
              class="form-control orcamento-item-edit__input text-right"
              v-model="item.margem_valor"
              placeholder="Margem"
            />
            <money
              v-show="!isMargemValor"
              id="margem_percentual"
              @blur.native="onBlurMargem_percentual"
              type="text"
              class="form-control orcamento-item-edit__input text-right"
              v-model="item.margem_percentual"
              placeholder="Margem"
            />
          </div>
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="valor_com_margem">Valor c/ Margem</label>
          <b-form-input
            id="valor_com_margem"
            type="text"
            :value="formatMoney(item.valor_unitario * 1 + item.margem_valor * 1)"
            readonly
            class="orcamento-item-edit__input text-right"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="valor_bruto">Valor Bruto</label>
          <b-form-input
            id="valor_bruto"
            type="text"
            :value="formatMoney((item.valor_unitario + item.margem_valor) * item.quantidade)"
            readonly
            class="orcamento-item-edit__input text-right"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="valor_ipi">Valor IPI (unit.)</label>
          <b-form-input
            id="valor_ipi"
            type="text"
            :value="formatMoney(parseFloat(item.valor_ipi) + item.valor_margem_ipi)"
            readonly
            class="orcamento-item-edit__input orcamento-item-edit__input--trib text-right"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label" for="valor_st">Valor ST (unit.)</label>
          <b-form-input
            id="valor_st"
            type="text"
            :value="formatMoney(parseFloat(item.valor_st) + item.valor_margem_st)"
            readonly
            class="orcamento-item-edit__input orcamento-item-edit__input--trib text-right"
          />
        </div>

        <div class="orcamento-item-edit__field">
          <label class="orcamento-item-edit__label orcamento-item-edit__label--total" for="valor_total">
            Valor Total
          </label>
          <div class="orcamento-item-edit__total-wrap">
            <b-form-input
              id="valor_total"
              type="text"
              :value="formatMoney(valorTotal)"
              readonly
              class="orcamento-item-edit__input orcamento-item-edit__input--total text-right"
            />
            <div class="orcamento-item-edit__loading" v-if="recalcularTributoMargem">
              <b-spinner variant="primary" small></b-spinner>
            </div>
          </div>
        </div>
      </div>

      <div class="orcamento-item-edit__alerts" v-if="!boVlMinimoAtingido || !isMargemDentroLimite()">
        <div class="orcamento-item-edit__alerta" v-if="!boVlMinimoAtingido">
          <p>* Esse produto possui regra de preço mínimo e seu valor não pode ser menor que {{ formatValorMoeda(item.vl_liquido_tab_coorp) }}</p>
          <p>* Para vendas abaixo do valor, apenas com registro de oportunidade. Entre em contato com seu consultor.</p>
        </div>
        <div class="orcamento-item-edit__alerta" v-if="!isMargemDentroLimite()">
          <p>*** Percentual máximo de margem é de {{ $store.state.configPadrao.limiteMaximoMargem }}%</p>
        </div>
      </div>

      <div id="loading-modal-itens" class="none-loading"></div>
    </template>

    <template v-slot:modal-footer>
      <b-button
        class="orcamento-item-edit__btn orcamento-item-edit__btn--cancel"
        :disabled="isSaving"
        @click="close()"
      >
        Cancelar
      </b-button>
      <b-button
        class="orcamento-item-edit__btn orcamento-item-edit__btn--save"
        :disabled="isSaving || !isMargemDentroLimite()"
        @click="salvarItem()"
      >
        <b-spinner v-if="isSaving" small></b-spinner>
        <span v-else>Salvar</span>
      </b-button>
    </template>
  </b-modal>
</template>

<style>
.orcamento-item-edit {
  border: none !important;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.18);
}

.orcamento-item-edit .modal-header.orcamento-item-edit__header {
  display: block;
  background: #0075eb;
  border-bottom: none;
  padding: 1rem 1.25rem;
}

.orcamento-item-edit .modal-body.orcamento-item-edit__body {
  padding: 1.35rem 1.25rem 1rem;
}

.orcamento-item-edit .modal-footer.orcamento-item-edit__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  border-top: 1px solid #edf0f2;
  padding: 0.9rem 1.25rem 1.1rem;
  background: #fff;
}
</style>

<style scoped>
.orcamento-item-edit__header-inner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.orcamento-item-edit__header-text {
  min-width: 0;
}

.orcamento-item-edit__eyebrow {
  display: block;
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.35rem;
}

.orcamento-item-edit__produto {
  margin: 0;
  color: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.35;
  text-transform: uppercase;
  word-break: break-word;
}

.orcamento-item-edit__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  cursor: pointer;
  transition: background 0.15s ease;
}

.orcamento-item-edit__close:hover:not(:disabled),
.orcamento-item-edit__close:focus:not(:disabled) {
  background: rgba(255, 255, 255, 0.22);
  outline: none;
}

.orcamento-item-edit__close:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.orcamento-item-edit__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem 0.85rem;
}

.orcamento-item-edit__field {
  min-width: 0;
}

.orcamento-item-edit__label {
  display: block;
  margin-bottom: 0.4rem;
  color: #8a94a6;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.orcamento-item-edit__label--total {
  color: #0075eb;
}

.orcamento-item-edit__input {
  height: 42px;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  background: #fff;
  color: #23282c;
  font-weight: 600;
  font-size: 0.92rem;
  box-shadow: none;
}

.orcamento-item-edit__input:focus {
  border-color: #0075eb;
  box-shadow: 0 0 0 0.15rem rgba(0, 117, 235, 0.15);
}

.orcamento-item-edit__input[readonly] {
  background: #f5f7fa;
}

.orcamento-item-edit__input--trib {
  border-color: #f0c48a;
  color: #e67e22;
}

.orcamento-item-edit__input--total {
  border-color: #0075eb;
  background: #f3f8ff;
  color: #0075eb;
  font-weight: 700;
}

.orcamento-item-edit__margem {
  display: flex;
  align-items: stretch;
  gap: 0.45rem;
}

.orcamento-item-edit__toggle {
  display: inline-flex;
  flex-shrink: 0;
  padding: 3px;
  border: 1px solid #d8dee6;
  border-radius: 8px;
  background: #f5f7fa;
}

.orcamento-item-edit__toggle-btn {
  min-width: 36px;
  height: 34px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #6c757d;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0 0.55rem;
}

.orcamento-item-edit__toggle-btn--active {
  background: #0075eb;
  color: #fff;
}

.orcamento-item-edit__margem >>> .orcamento-item-edit__input {
  flex: 1;
  min-width: 0;
}

.orcamento-item-edit__total-wrap {
  position: relative;
}

.orcamento-item-edit__loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  border-radius: 8px;
}

.orcamento-item-edit__alerts {
  margin-top: 1rem;
}

.orcamento-item-edit__alerta p {
  margin: 0 0 0.25rem;
  color: #c62828;
  font-size: 0.78rem;
  line-height: 1.35;
}

.orcamento-item-edit__btn {
  min-width: 96px;
  border-radius: 8px !important;
  font-weight: 600;
  padding: 0.45rem 1.1rem;
}

.orcamento-item-edit__btn--cancel {
  background: #fff !important;
  border: 1px solid #d8dee6 !important;
  color: #6c757d !important;
}

.orcamento-item-edit__btn--cancel:hover:not(:disabled),
.orcamento-item-edit__btn--cancel:focus:not(:disabled) {
  background: #f8f9fa !important;
  color: #495057 !important;
}

.orcamento-item-edit__btn--save {
  background: #0075eb !important;
  border: 1px solid #0075eb !important;
  color: #fff !important;
}

.orcamento-item-edit__btn--save:hover:not(:disabled),
.orcamento-item-edit__btn--save:focus:not(:disabled) {
  background: #005bb7 !important;
  border-color: #005bb7 !important;
}

.show-loading {
  display: block;
  background: black;
  opacity: 0.3;
  height: 100vh;
  width: 100vw;
  z-index: 4;
  position: fixed;
  top: 0;
  left: 0;
}

.none-loading {
  display: none;
}

@media (max-width: 767.98px) {
  .orcamento-item-edit__grid {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575.98px) {
  .orcamento-item-edit__grid {
    grid-template-columns: 1fr;
  }
}
</style>

<script>
import Orcamento from "../../../services/orcamento";
import Produto from "@/services/produto";

export default {
  name: "OrcamentoItemEdit",
  props: {
    id: {
      type: String,
      default: "OrcamentoItemEdit"
    },
    value: {
      type: Object,
      default: function() {
        return new Object();
      }
    },
    orcamento: {
      type: Object,
      default: function() {
        return new Object();
      }
    },
    codigoCliente: {
      type: Number,
      default: 0
    },
    idOrcamento: {
      type: Number,
      default: 0
    }
  },
  data: () => ({
    isSaving: false,
    item: new Object(),
    margemTipo: "V",
    recalcularTributoMargem: false,
    boVlMinimoAtingido: true,
    marcas_isentas: [86, 87, 187, 237, 257, 288, 320, 321],
    valor_margem_original: 0,
    margem_percentual_original: 0,
    valor_ipi_original: 0,
    valor_st_original: 0,
    valor_margem_ipi_original: 0,
    valor_margem_st_original: 0,
    vl_liquido_tab_coorp_original: 0
  }),
  computed: {
    isMargemValor() {
      return this.margemTipo == "V";
    },
    valorTotal() {
      const unitario = parseFloat(this.item.valor_unitario) || 0;
      const margem = parseFloat(this.item.margem_valor) || 0;
      const quantidade = parseFloat(this.item.quantidade) || 0;
      const ipi = (parseFloat(this.item.valor_ipi) || 0) + (parseFloat(this.item.valor_margem_ipi) || 0);
      const st = (parseFloat(this.item.valor_st) || 0) + (parseFloat(this.item.valor_margem_st) || 0);
      return (unitario + margem) * quantidade + ipi * quantidade + st * quantidade;
    },
    formatValorMoeda() {
      return valor =>
        Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(valor);
    }
  },
  watch: {
    value: function(value) {
      this.item = JSON.parse(JSON.stringify(value));

      if (!this.isMargemDentroLimite()) {
        this.item.margem_valor = this.item.ultimaMargem.margemValor;
        this.item.margem_percentual = this.item.ultimaMargem.margemPercentual;
      }

      this.valor_margem_original = this.item.ultimaMargem.margemValor;
      this.margem_percentual_original = this.item.ultimaMargem.margemPercentual;
      this.valor_ipi_original = this.item.valor_ipi;
      this.valor_st_original = this.item.valor_st;
      this.valor_margem_ipi_original = this.item.valor_margem_ipi;
      this.valor_margem_st_original = this.item.valor_margem_st;
      this.vl_liquido_tab_coorp_original = this.item.vl_liquido_tab_coorp;

      if (!this.valida_vl_pscf()) {
        this.boVlMinimoAtingido = false;
        return;
      }

      this.boVlMinimoAtingido = true;
    }
  },
  mounted() {
    this.margem();
  },
  methods: {
    formatMoney(value) {
      if (!value && value !== 0) {
        return "R$ 0,00";
      }
      return this.formatValorMoeda(value);
    },
    setMargemTipo(tipo) {
      if (this.margemTipo === tipo) {
        return;
      }
      this.margemTipo = tipo;
      this.margem();
    },
    onBlurMargem_valor(e) {
      if (e.relatedTarget && e.relatedTarget.name === "margemTipoRadio") {
        return;
      }
      if (this.isMargemValor && this.isMargemDentroLimite()) {
        this.recalcularTributoMargem = true;
        this.item.margem_percentual =
          ((this.item.margem_valor * 1) / (this.item.valor_unitario * 1)) * 100;
        this.valida_vl_pscf();
        this.buscarValorImpostoMargem();
      }
    },
    onBlurMargem_percentual(e) {
      if (e.relatedTarget && e.relatedTarget.name === "margemTipoRadio") {
        return;
      }
      if (!this.isMargemValor && this.isMargemDentroLimite()) {
        this.recalcularTributoMargem = true;
        this.item.margem_valor =
          this.item.valor_unitario * 1 * ((this.item.margem_percentual * 1) / 100);
        this.item.margem_valor = Math.round(this.item.margem_valor * 100) / 100;
        this.valida_vl_pscf();
        this.buscarValorImpostoMargem();
      } else {
        return;
      }
    },
    isMargemDentroLimite() {
      let limite = 0;
      let boMargemDentroLimite = false;
      let limiteMaximoMargem =
        this.$store.state.configPadrao.limiteMaximoMargem +
        this.$store.state.configPadrao.limiteMaximoDivergenciaMargemOrc;

      if (this.isMargemValor) {
        limite = Math.round(this.item.valor_unitario * (limiteMaximoMargem / 100) * 100) / 100;
        boMargemDentroLimite = this.item.margem_valor <= limite;
      }

      if (!this.isMargemValor) {
        boMargemDentroLimite =
          Math.round(this.item.margem_percentual * 100) / 100 <= limiteMaximoMargem;
      }

      if (this.item.bo_valida_minimo == "S") {
        boMargemDentroLimite = true;
      }

      return boMargemDentroLimite;
    },
    close() {
      this.recalcularTributoMargem = false;
      this.isSaving = false;
      this.$refs["orcamentoItemEditModal"].hide();

      Produto.getValoresMargem(
        this.item.produto_codigo,
        this.codigoCliente,
        this.margem_percentual_original,
        this.valor_margem_original,
        this.item.quantidade,
        this.item.cd_filial,
        this.idOrcamento
      )
        .then(response => {
          this.item.valor_ipi = response.data.data.valor_ipi;
          this.item.valor_st = response.data.data.valor_st;
          this.item.valor_margem_ipi = response.data.data.valor_margem_ipi;
          this.item.valor_margem_st = response.data.data.valor_margem_st;
          this.item.bo_valida_minimo = response.data.data.bo_valida_vl_minimo_tab_coorp;
          this.item.vl_liquido_tab_coorp = response.data.data.vl_liquido_tab_coorp;
        })
        .catch(e => {
          this.$helper.showErrorResponse(e);
        });
    },
    buscarValorImpostoMargem() {
      let vue = this;
      let obj_loading = document.getElementById("loading-modal-itens");
      obj_loading.classList.add("show-loading");
      obj_loading.classList.remove("none-loading");
      Produto.getValoresMargem(
        this.item.produto_codigo,
        this.codigoCliente,
        this.item.margem_percentual,
        this.item.margem_valor,
        this.item.quantidade,
        this.item.cd_filial,
        this.idOrcamento
      )
        .then(response => {
          this.item.valor_ipi = response.data.data.valor_ipi;
          this.item.valor_st = response.data.data.valor_st;
          this.item.valor_margem_ipi = response.data.data.valor_margem_ipi;
          this.item.valor_margem_st = response.data.data.valor_margem_st;
          this.item.bo_valida_minimo = response.data.data.bo_valida_vl_minimo_tab_coorp;
          this.item.vl_liquido_tab_coorp = response.data.data.vl_liquido_tab_coorp;
        })
        .catch(e => {
          this.$helper.showErrorResponse(e);
        })
        .finally(() => {
          vue.recalcularTributoMargem = false;
          this.isSaving = false;
          document.getElementById("orcamentoItemEdit___BV_modal_content_").focus();
          obj_loading.classList.add("none-loading");
          obj_loading.classList.remove("show-loading");
        });
    },
    validaSalvarPsd() {
      if (this.item.bo_registro == "false") {
        if (this.item.bo_valida_minimo == "S") {
          let valor =
            parseFloat(this.item.valor_unitario.toFixed(2)) +
            parseFloat(this.item.margem_valor.toFixed(2));
          let valor_tabela5 = parseFloat(this.item.vl_liquido_tab_coorp.toFixed(2));

          if (valor < valor_tabela5) {
            this.boVlMinimoAtingido = false;
            this.isSaving = false;
            return false;
          } else {
            this.margem();
            return true;
          }
        } else {
          this.margem();
          return true;
        }
      } else {
        this.margem();
        return true;
      }
    },
    salvarItem() {
      let valida = this.validaSalvarPsd();

      if (valida) {
        this.isSaving = true;

        Orcamento.saveOrcamentoItem(this.item)
          .then(() => {
            this.$helper.showMsg("Alterações salvas com sucesso!", "success");
            this.item.valor_total =
              this.item.quantidade *
              1 *
              (this.item.valor_unitario * 1 +
                this.item.valor_ipi * 1 +
                this.item.valor_st * 1 +
                this.item.margem_valor * 1);
            this.$emit("itemSaved", this.item);
            this.$refs["orcamentoItemEditModal"].hide();
          })
          .catch(e => {
            this.$helper.showErrorResponse(e);
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    },
    valida_vl_pscf() {
      if (
        parseFloat(this.item.vl_liquido_tab_coorp) != 0 &&
        this.item.bo_registro == "false"
      ) {
        if (this.item.vl_liquido_tab_coorp > this.item.valor_unitario + this.item.margem_valor) {
          let valor_indicado =
            parseFloat(this.item.vl_liquido_tab_coorp) -
            (parseFloat(this.item.valor_unitario) + parseFloat(this.item.margem_valor));

          this.item.margem_valor = valor_indicado + this.item.margem_valor;
          this.item.margem_percentual =
            ((this.item.margem_valor * 1) / (this.item.valor_unitario * 1)) * 100;
          this.buscarValorImpostoMargem();

          return false;
        }
      } else {
        return true;
      }
    },
    margem() {
      this.item.margem_percentual =
        ((this.item.margem_valor * 1) / (this.item.valor_unitario * 1)) * 100;

      if (
        parseFloat(this.item.vl_liquido_tab_coorp) != 0 &&
        this.item.bo_registro == "false"
      ) {
        if (this.item.vl_liquido_tab_coorp > this.item.valor_unitario + this.item.margem_valor) {
          let valor_indicado =
            parseFloat(this.item.vl_liquido_tab_coorp) -
            (parseFloat(this.item.valor_unitario) + parseFloat(this.item.margem_valor));

          this.item.margem_valor = valor_indicado + this.item.margem_valor;
        } else {
          this.item.margem_valor =
            this.item.valor_unitario * 1 * ((this.item.margem_percentual * 1) / 100);
          this.item.margem_valor = Math.round(this.item.margem_valor * 100) / 100;
        }
      } else {
        this.item.margem_valor =
          this.item.valor_unitario * 1 * ((this.item.margem_percentual * 1) / 100);
        this.item.margem_valor = Math.round(this.item.margem_valor * 100) / 100;
      }
    }
  }
};
</script>
